import {
  ArrowLongRightIcon,
  FolderIcon,
  PencilIcon,
  PlusIcon as Plus,
  ShareIcon,
  TrashIcon,
  UsersIcon,
  DocumentTextIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { useDeleteFolder } from 'containers/Sequences/queries/useDeleteFolder';
import { useOpenDeleteFolderWarningModal } from 'containers/Sequences/DeleteFolderWarningModal/atoms';
import { v4 as uuid } from 'uuid';
import { useOpenRenameFolderModal } from 'containers/Sequences/List/Sidebar/RenameFolderModal/atoms';
import { useOpenShareSequenceModal } from 'containers/Sequences/ShareSequenceModal/atoms';
import useDuplicateSequence from 'queries/sequences/useDuplicateSequence';
import useDeleteSequence from 'queries/sequences/useDeleteSequence';
import { FolderNode } from 'types/sequences';
import { useCreateFicheDePrep } from 'queries/fdp/useCreateFichedePrep';
import { useNavigate } from 'react-router-dom';
import { useCreateFolder } from 'containers/Sequences/queries/useCreateFolder';
import { useDuplicateFicheDePrep } from 'queries/fdp/useDuplicateFichedePrep';
import useDeleteFicheDePrep from 'queries/fdp/useDeleteFicheDePrep';
import { useOpenAllFoldersWithOrWithoutSequencesModal } from 'containers/Sequences/ListAllFoldersWithOrWithoutSequencesModal/atoms';
import { useUpdateSequencesFolders } from 'queries/sequences/useUpdateSequencesFolders';
import { useUpdateSequenceOfFicheDePrep } from 'queries/fdp/useUpdateFicheDePrepSequence';
import { useTranslation } from 'react-i18next';
import { useOpenProposeSequenceModal } from '../ProposeSequenceModal/atoms';
import { useFeatureFlagEnabled } from 'utils/useFeatureFlagEnabled';
import emptyBo from 'components/Bo/emptyBo';
import { useCreateStandaloneFicheDePrep } from 'queries/fdp/useCreateStandaloneFicheDePrep';
import { useMoveStandaloneFicheDePrep } from 'queries/fdp/useMoveStandaloneFicheDePrep';
import { useDuplicateStandaloneFicheDePrep } from 'queries/fdp/useDuplicateStandaloneFicheDePrep';
import { useDeleteStandaloneFicheDePrep } from 'queries/fdp/useDeleteStandaloneFicheDePrep';
import { DuplicateIcon } from 'components/Icons/icons/Duplicate';
import { useCreateEmptySequence } from 'queries/sequences/useCreateSequence';

export interface Action {
  icon: any;
  title: string;
  intent?: 'danger';
  onClick: (folder: FolderNode) => any;
}

export function useGetMenuBasedOnFolderType(
  hiddenActions?: 'all',
): (folder: FolderNode) => Action[] {
  const deleteFolder = useDeleteFolder();
  const navigate = useNavigate();
  const openDeleteWarningModal = useOpenDeleteFolderWarningModal();
  const openRenameFolderModal = useOpenRenameFolderModal();
  const openShareSequenceModal = useOpenShareSequenceModal();
  const openProposeSequenceModal = useOpenProposeSequenceModal();
  const duplicateSequence = useDuplicateSequence();
  const deleteSequence = useDeleteSequence();
  const createFdp = useCreateFicheDePrep();
  const openAllFoldersWithOrWithoutSequencesModal =
    useOpenAllFoldersWithOrWithoutSequencesModal();
  const createEmptySequence = useCreateEmptySequence();
  const createFolder = useCreateFolder();
  const duplicateFdp = useDuplicateFicheDePrep();
  const deleteFdp = useDeleteFicheDePrep();
  const updateSequencesFolders = useUpdateSequencesFolders();
  const updateSequenceOfFicheDePrep = useUpdateSequenceOfFicheDePrep();
  const { t } = useTranslation(['common', 'sequence']);
  const isShareSequenceToCommunityFlagEnabled = useFeatureFlagEnabled(
    'allow-share-sequence-to-community',
  );
  const { mutateAsync: createFdpStandalone } = useCreateStandaloneFicheDePrep();
  const { mutateAsync: moveFicheDePrep } = useMoveStandaloneFicheDePrep();
  const { mutateAsync: duplicateStandaloneFdp } =
    useDuplicateStandaloneFicheDePrep();
  const { mutateAsync: deleteStandaloneFdp } = useDeleteStandaloneFicheDePrep();

  const onDeleteFdp = ({
    sequenceId,
    ficheDePrepId,
  }: {
    sequenceId: string;
    ficheDePrepId: string;
  }) =>
    deleteFdp({
      fdpId: ficheDePrepId as string,
      sequenceId: sequenceId as string,
    }).then(() => navigate(`/sequences/${sequenceId as string}`));
  const onCreateFdp = ({ sequenceId }) =>
    createFdp({
      sequenceId,
      // we should not use empty bo here because the back is going to populate that with the one of the sequence
      ficheDePrep: {
        id: uuid(),
        sequenceId,
        niveaux: [],
        materials: [],
        title: '',
        attachments: [],
        phases: [],
      },
    }).then(res =>
      navigate(`/sequences/${sequenceId}/fiches-de-prep/${res?.id}`),
    );

  const onCreateStandaloneFdp = ({ folderId }) =>
    createFdpStandalone({
      folderId,
      ficheDePrep: {
        id: uuid(),
        sequenceId: uuid(),
        bo: emptyBo,
        niveaux: [],
        materials: [],
        title: '',
        attachments: [],
        phases: [],
      },
    }).then(res => navigate(`/fiches-de-prep/${res?.id}`));

  const onCreateSequence = async ({ folderId }) => {
    await createEmptySequence({ folderId });
  };

  const onCreateFolder = (folderId: string) => {
    const newFolderId = uuid();
    return createFolder({
      id: newFolderId,
      name: t('sequence:actions.newFolder'),
      parentId: folderId === 'root' ? undefined : folderId,
    }).then(() => navigate(`/sequences/folders/${newFolderId}?new=true`));
  };

  if (hiddenActions === 'all') {
    return () => [];
  }

  return (folder: FolderNode) => {
    if (folder.type === 'folder') {
      return [
        {
          icon: RectangleStackIcon,
          title: t('sequence:actions.addSequence'),
          onClick: async f => onCreateSequence({ folderId: f.id }),
        },
        {
          icon: DocumentTextIcon,
          title: t('sequence:actions.addFicheDePrep'),
          onClick: async f => onCreateStandaloneFdp({ folderId: f.id }),
        },
        {
          icon: FolderIcon,
          title: t('sequence:actions.addFolder'),
          onClick: async f => {
            await onCreateFolder(f.id);
          },
        },
        ...[
          {
            icon: PencilIcon,
            title: t('common:buttons.rename'),
            onClick: async e => {
              openRenameFolderModal({
                folderId: e.id,
                currentName: e.title,
              });
            },
          },
        ],
        ...[
          {
            icon: ArrowLongRightIcon,
            title: t('sequence:actions.move'),
            onClick: (f: FolderNode) => {
              openAllFoldersWithOrWithoutSequencesModal({
                title: f.title || t(`sequence:fileTree.defaultLabel.${f.type}`),
                onChangeFolder: (folderId: string) =>
                  updateSequencesFolders({
                    items: [
                      {
                        id: f.id,
                        type: f.type,
                        destFolderId:
                          folderId === 'root' ? undefined : folderId,
                      },
                    ],
                  }),
              });
            },
          },
        ],
        {
          icon: TrashIcon,
          title: t('common:buttons.delete'),
          intent: 'danger',
          onClick: async e => {
            if (folder?.id === e.id) {
              if ((folder?.children || []).length > 0) {
                return openDeleteWarningModal({
                  folder,
                });
              }
              await deleteFolder({ folderId: e.id });
              return;
            }
            const matchingFolder = folder?.children?.find(f => f.id === e.id);
            if (!matchingFolder) {
              return;
            }
            if ((matchingFolder?.children || []).length > 0) {
              return openDeleteWarningModal({
                folder: matchingFolder,
              });
            }
            await deleteFolder({ folderId: e.id });
          },
        },
      ];
    }
    if (folder.type === 'fdp') {
      return [
        {
          icon: DuplicateIcon,
          title: t('common:buttons.duplicate'),
          ['aria-label']: 'Dupliquer fiche de prep',
          onClick: async () => {
            await duplicateFdp({
              sequenceId: folder.parentId!,
              ficheDePrepId: folder.id,
            });
          },
        },
        {
          icon: ArrowLongRightIcon,
          title: t('sequence:actions.moveInAnotherSequence'),
          onClick: () => {
            openAllFoldersWithOrWithoutSequencesModal({
              title:
                folder.title ||
                t(`sequence:fileTree.defaultLabel.${folder.type}`),
              onChangeSequence: (sequenceID: string) =>
                updateSequenceOfFicheDePrep({
                  srcSequenceId: folder.parentId!,
                  destSequenceId: sequenceID,
                  ficheDePrepId: folder.id,
                }).then(() => {
                  const currUrl = window.location.pathname;
                  if (currUrl.includes(folder.id)) {
                    // if we are on the FDP URL, we need to redirect to the new URL
                    const nextUrl = currUrl.replace(
                      folder.parentId!,
                      sequenceID,
                    );
                    navigate(nextUrl);
                  }
                  return;
                }),
            });
          },
        },
        {
          icon: TrashIcon,
          title: t('common:buttons.delete'),
          intent: 'danger',
          onClick: async () => {
            await onDeleteFdp({
              sequenceId: folder.parentId!,
              ficheDePrepId: folder.id,
            });
          },
        },
      ];
    }
    if (folder.type === 'sequence') {
      return [
        {
          icon: Plus,
          title: t('sequence:actions.addSeance'),
          onClick: async f => {
            await onCreateFdp({
              sequenceId: f.id,
            });
          },
        },
        ...[
          {
            icon: ArrowLongRightIcon,
            title: t('sequence:actions.moveInAFolder'),
            onClick: (f: FolderNode) => {
              openAllFoldersWithOrWithoutSequencesModal({
                title: f.title || t(`sequence:fileTree.defaultLabel.${f.type}`),
                onChangeFolder: (folderId: string) =>
                  updateSequencesFolders({
                    items: [
                      {
                        id: f.id,
                        type: f.type,
                        destFolderId:
                          folderId === 'root' ? undefined : folderId,
                      },
                    ],
                  }),
                onCreateFolder: (folderName: string) =>
                  createFolder({
                    id: uuid(),
                    name: folderName,
                    sequenceId: f.type === 'sequence' ? f.id : undefined,
                  }),
              });
            },
          },
        ],
        ...[
          {
            icon: ShareIcon,
            title: t('common:buttons.share'),
            onClick: async f => {
              openShareSequenceModal({
                sequenceId: f.id,
                sequenceTitle: f.title,
              });
            },
          },
        ],
        ...(!isShareSequenceToCommunityFlagEnabled
          ? []
          : [
              {
                icon: UsersIcon,
                title: t('common:buttons.shareToCommunity'),
                onClick: async f => {
                  openProposeSequenceModal({
                    sequenceId: f.id,
                    sequenceTitle: f.title,
                  });
                },
              },
            ]),
        {
          icon: DuplicateIcon,
          title: t('common:buttons.duplicate'),
          onClick: async f => {
            await duplicateSequence(f.id);
          },
        },
        {
          icon: TrashIcon,
          title: t('common:buttons.delete'),
          intent: 'danger',
          onClick: async f => {
            await deleteSequence({ sequenceId: f.id });
          },
        },
      ];
    }
    if (folder.type === 'fdp_standalone') {
      return [
        {
          icon: DuplicateIcon,
          title: t('common:buttons.duplicate'),
          ['aria-label']: 'Dupliquer fiche de prep',
          onClick: async () => {
            await duplicateStandaloneFdp({
              ficheDePrepId: folder.id,
              folderId: folder.parentId,
            });
          },
        },
        {
          icon: ArrowLongRightIcon,
          title: t('sequence:actions.move'),
          onClick: () => {
            openAllFoldersWithOrWithoutSequencesModal({
              title:
                folder.title ||
                t(`sequence:fileTree.defaultLabel.${folder.type}`),
              onChangeSequence: (sequenceID: string) =>
                moveFicheDePrep({
                  ficheDePrepId: folder.id,
                  targetSequenceId: sequenceID,
                }),
              onChangeFolder: (folderId: string) =>
                moveFicheDePrep({
                  ficheDePrepId: folder.id,
                  targetFolderId: folderId === 'root' ? undefined : folderId,
                }),
            });
          },
        },
        {
          icon: TrashIcon,
          title: t('common:buttons.delete'),
          intent: 'danger',
          onClick: async () => {
            await deleteStandaloneFdp({
              ficheDePrepId: folder.id,
            });
          },
        },
      ];
    }
    return [];
  };
}
